import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Settings, Loader2, Wallet, CreditCard } from "lucide-react";
import { toast } from "sonner";
import adminService from "../services/admin.service";

interface AdminHostelSettingsProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface SettingsForm {
  pricePerAcademicYear: string;
  momoSurchargePercentage: string;
  academicYearMoveInDate: string;
  bankName: string;
  accountNumber: string;
  accountName: string;
  branch: string;
  momoNumber: string;
  momoName: string;
}

const EMPTY_FORM: SettingsForm = {
  pricePerAcademicYear: "",
  momoSurchargePercentage: "",
  academicYearMoveInDate: "",
  bankName: "",
  accountNumber: "",
  accountName: "",
  branch: "",
  momoNumber: "",
  momoName: "",
};

export function AdminHostelSettings({ open, onOpenChange }: AdminHostelSettingsProps) {
  const [formData, setFormData] = useState<SettingsForm>(EMPTY_FORM);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      fetchConfig();
    }
  }, [open]);

  const fetchConfig = async () => {
    setLoading(true);
    try {
      const config = await adminService.getConfig();
      const payment = config.paymentDetails || {};
      setFormData({
        pricePerAcademicYear:
          config.pricePerAcademicYear != null ? String(config.pricePerAcademicYear) : "",
        momoSurchargePercentage:
          config.momoSurchargePercentage != null ? String(config.momoSurchargePercentage) : "1",
        academicYearMoveInDate: config.academicYearMoveInDate || "2026-10-05",
        bankName: payment.bankName || "",
        accountNumber: payment.accountNumber || "",
        accountName: payment.accountName || "",
        branch: payment.branch || "",
        momoNumber: payment.momoNumber || "",
        momoName: payment.momoName || "",
      });
    } catch (error: any) {
      toast.error(error.message || "Failed to load hostel settings");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: keyof SettingsForm) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { value } = e.target;
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    const price = Number(formData.pricePerAcademicYear);
    if (!formData.pricePerAcademicYear || isNaN(price) || price < 0) {
      toast.error("Price per academic year must be a non-negative number");
      return;
    }

    const momoSurcharge = Number(formData.momoSurchargePercentage);
    if (
      !formData.momoSurchargePercentage ||
      isNaN(momoSurcharge) ||
      momoSurcharge < 0 ||
      momoSurcharge > 100
    ) {
      toast.error("Mobile Money surcharge must be between 0 and 100");
      return;
    }

    if (!formData.academicYearMoveInDate) {
      toast.error("Please set the academic year move-in date");
      return;
    }

    setSaving(true);
    try {
      await adminService.updateConfig({
        pricePerAcademicYear: price,
        momoSurchargePercentage: momoSurcharge,
        academicYearMoveInDate: formData.academicYearMoveInDate,
        paymentDetails: {
          bankName: formData.bankName.trim(),
          accountNumber: formData.accountNumber.trim(),
          accountName: formData.accountName.trim(),
          branch: formData.branch.trim(),
          momoNumber: formData.momoNumber.trim(),
          momoName: formData.momoName.trim(),
        },
      });
      toast.success("Hostel settings updated!");
      onOpenChange(false);
    } catch (error: any) {
      toast.error(error.message || "Failed to update hostel settings");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-[#0f1f3d] flex items-center gap-2">
            <Settings className="w-6 h-6 text-[#d4af37]" />
            Hostel Settings
          </DialogTitle>
          <DialogDescription>
            Set the academic-year price and the payment details students see when booking.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-[#d4af37]" />
          </div>
        ) : (
          <div className="space-y-6 py-2">
            {/* Pricing */}
            <div className="space-y-4">
              <p className="font-medium text-[#0f1f3d] flex items-center gap-2">
                <Wallet className="w-4 h-4 text-[#d4af37]" />
                Pricing
              </p>
              <div className="space-y-2">
                <Label htmlFor="pricePerAcademicYear">Price per academic year (GH₵, per head) *</Label>
                <Input
                  id="pricePerAcademicYear"
                  type="number"
                  min={0}
                  value={formData.pricePerAcademicYear}
                  onChange={handleChange("pricePerAcademicYear")}
                  placeholder="6500"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="academicYearMoveInDate">Academic year move-in date *</Label>
                <Input
                  id="academicYearMoveInDate"
                  type="date"
                  value={formData.academicYearMoveInDate}
                  onChange={handleChange("academicYearMoveInDate")}
                />
                <p className="text-xs text-gray-500">
                  The date new bookings start (academic-year commencement).
                </p>
              </div>
            </div>

            {/* Payment details */}
            <div className="space-y-4">
              <p className="font-medium text-[#0f1f3d] flex items-center gap-2">
                <CreditCard className="w-4 h-4 text-[#d4af37]" />
                Payment Details
              </p>
              <p className="text-sm text-gray-500">
                Shown to students in the booking flow instead of "contact the manager".
              </p>
              <div className="space-y-2">
                <Label htmlFor="bankName">Bank name</Label>
                <Input id="bankName" value={formData.bankName} onChange={handleChange("bankName")} placeholder="e.g. GCB Bank" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="accountNumber">Account number</Label>
                <Input id="accountNumber" value={formData.accountNumber} onChange={handleChange("accountNumber")} placeholder="e.g. 1234567890" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="accountName">Account name</Label>
                <Input id="accountName" value={formData.accountName} onChange={handleChange("accountName")} placeholder="e.g. Kwasi Ofei Kyei" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="branch">Branch</Label>
                <Input id="branch" value={formData.branch} onChange={handleChange("branch")} placeholder="e.g. Bibiani Branch" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="momoNumber">Mobile money number</Label>
                <Input id="momoNumber" value={formData.momoNumber} onChange={handleChange("momoNumber")} placeholder="e.g. 024 123 4567" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="momoName">Mobile money name</Label>
                <Input id="momoName" value={formData.momoName} onChange={handleChange("momoName")} placeholder="e.g. Majesty Hostel" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="momoSurchargePercentage">Mobile Money surcharge (%) *</Label>
                <Input
                  id="momoSurchargePercentage"
                  type="number"
                  min={0}
                  max={100}
                  step="any"
                  value={formData.momoSurchargePercentage}
                  onChange={handleChange("momoSurchargePercentage")}
                  placeholder="1"
                />
                <p className="text-xs text-gray-500">
                  Extra fee students pay when using Mobile Money, shown to them on the payment step.
                </p>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
                Cancel
              </Button>
              <Button variant="gold" onClick={handleSave} disabled={saving}>
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  "Save Settings"
                )}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
